// Turning patches into outbox entries (WEB_PLAN.md §3.6 "Making an edit"). Several edits to one item before it is
// pushed collapse into one entry: the patch applies to the entry's local payload, the base payload/version stay the
// ones the first edit started from, and the edit time moves to the latest edit.

import type { Collection } from '@/contracts/collections';
import { FILE_COLLECTIONS, SETTINGS_ID } from '@/contracts/collections';
import { isUuid, sameId } from '@/contracts/uuid';
import type { Json, JsonObject } from '@/contracts/wire';
import { toEnvelopeDate } from '@/contracts/wireDate';
import { canonicalPayload, isJsonObject, keepsMergeBase } from './canonical';
import { deepEqual, fieldEqual } from './merge';
import type { Patch } from './patch';
import type { OutboxEntry, Replica } from './replica';
import { itemKey } from './runtime';

export interface SaveResult {
  /** Items whose outbox entry was created, changed or dropped. */
  changed: number;
  /** Patches that could not apply: an update or delete of an item that is gone (deleted elsewhere) or being deleted. */
  skipped: { collection: Collection; id: string; reason: 'missing' | 'deleted' }[];
}

/**
 * Validates a payload as JSON the way iOS will read it and normalises it in place: keys whose value is `undefined`
 * are removed (a nil optional is an omitted key). Throws on NaN/Infinity, `undefined` inside arrays, and anything
 * that is not plain JSON (Date, Map, class instances…), which JSON.stringify would silently turn into something else.
 */
export function assertJsonPayload(value: JsonObject, path = 'payload'): JsonObject {
  normalise(value, path);
  return value;
}

function normalise(value: unknown, path: string): void {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new TypeError(`${path} is not a finite number`);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((v, i) => {
      if (v === undefined) throw new TypeError(`${path}[${i}] is undefined`);
      normalise(v, `${path}[${i}]`);
    });
    return;
  }
  if (typeof value === 'object') {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) throw new TypeError(`${path} is not a plain JSON object`);
    const obj = value as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      if (obj[key] === undefined) delete obj[key];
      else normalise(obj[key], `${path}.${key}`);
    }
    return;
  }
  throw new TypeError(`${path} is not JSON (${typeof value})`);
}

/** What `toISOString()` writes. iOS 17's `.iso8601` decoder rejects fractional seconds (WEB_PLAN.md §0). */
const FRACTIONAL_SECONDS_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+Z$/;

/**
 * The §0 rules whose breach only shows on an iPhone (the item becomes undecodable there and is skipped): a top-level
 * `null` (a nil optional is an omitted key) and a date with milliseconds (`toISOString` instead of `toWireDate`).
 * Only keys that differ from `before` are checked, so whatever the server already holds never blocks an edit.
 */
export function assertWireValues(collection: Collection, next: JsonObject, before: JsonObject | null): void {
  for (const key of Object.keys(next)) {
    const value = next[key];
    if (before && Object.prototype.hasOwnProperty.call(before, key) && fieldEqual(key, value, before[key])) continue;
    if (value === null) throw new TypeError(`${collection}.${key} is null: delete the key to clear an optional (iOS omits nil values).`);
    assertWholeSecondDates(value, `${collection}.${key}`);
  }
}

function assertWholeSecondDates(value: Json, path: string): void {
  if (typeof value === 'string') {
    if (FRACTIONAL_SECONDS_DATE.test(value)) {
      throw new TypeError(`${path} is "${value}": payload dates are whole seconds (toWireDate), never toISOString().`);
    }
  } else if (Array.isArray(value)) {
    value.forEach((v, i) => assertWholeSecondDates(v, `${path}[${i}]`));
  } else if (value !== null && typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) assertWholeSecondDates(v, `${path}.${k}`);
  }
}

/**
 * A new item's identity as iOS reads it: a UUID envelope id equal to `payload.id` (the settings singleton has the fixed
 * SETTINGS_ID and no payload id), and for attachments/documents a `file_path` of `<company lowercase>/<collection>/
 * <payload.fileName>` (SyncEngine.storagePath; phones download the file to `fileName`).
 */
function assertNewItem(collection: Collection, id: string, payload: JsonObject, filePath: string | undefined): void {
  if (!isUuid(id)) throw new TypeError(`Cannot create ${collection}: "${id}" is not a UUID.`);
  if (collection === 'settings') {
    if (!sameId(id, SETTINGS_ID)) throw new TypeError(`The settings item's id is ${SETTINGS_ID}.`);
  } else if (typeof payload.id !== 'string' || !sameId(payload.id, id)) {
    throw new TypeError(`Cannot create ${collection} ${id}: payload.id must be the same id.`);
  }
  if (filePath === undefined) return;
  if (!FILE_COLLECTIONS.includes(collection)) throw new TypeError(`${collection} items carry no file (file_path).`);
  const parts = filePath.split('/');
  const fileName = payload.fileName;
  if (parts.length !== 3 || !parts[0] || parts[0] !== parts[0].toLowerCase() || parts[1] !== collection || typeof fileName !== 'string' || parts[2] !== fileName) {
    throw new TypeError(`file_path must be <company lowercase>/${collection}/<payload.fileName>, got "${filePath}".`);
  }
}

/**
 * Applies patches to the outbox atomically: every patch is computed against the state left by the previous ones and
 * nothing is stored if any of them throws (a bad payload, a create for an id that exists).
 */
export function applyPatches(replica: Replica, patches: readonly Patch[], now: () => Date): SaveResult {
  const staged = new Map<string, { collection: Collection; id: string; entry: OutboxEntry | null }>();
  const result: SaveResult = { changed: 0, skipped: [] };
  const current = (collection: Collection, id: string): OutboxEntry | undefined => {
    const s = staged.get(itemKey(collection, id));
    if (s) return s.entry ?? undefined;
    return replica.getEntry(collection, id);
  };
  const stage = (collection: Collection, id: string, entry: OutboxEntry | null) => {
    staged.set(itemKey(collection, id), { collection, id, entry });
  };
  let rev = replica.meta.editSeq;
  const nextRev = () => ++rev;

  for (const p of patches) {
    const { collection, id } = p;
    const row = replica.getRow(collection, id);
    const entry = current(collection, id);
    const modifiedAt = toEnvelopeDate(now());

    switch (p.kind) {
      case 'create': {
        if (row || entry) throw new Error(`Cannot create ${collection} ${id}: an item with this id already exists.`);
        if (!isJsonObject(p.payload)) throw new TypeError('create payload must be a JSON object');
        const payload = canonicalPayload(collection, assertJsonPayload(structuredClone(p.payload)));
        assertNewItem(collection, id, payload, p.filePath);
        assertWireValues(collection, payload, null);
        const next: OutboxEntry = {
          collection,
          id,
          kind: 'save',
          basePayload: null,
          baseVersion: null,
          localPayload: payload,
          modifiedAt,
          attempts: 0,
          state: 'pending',
          rev: nextRev(),
        };
        if (p.filePath !== undefined) next.filePath = p.filePath;
        stage(collection, id, next);
        break;
      }
      case 'update': {
        if (entry?.kind === 'delete') {
          result.skipped.push({ collection, id, reason: 'deleted' });
          break;
        }
        const source = entry ? entry.localPayload : row?.payload;
        if (!source) {
          result.skipped.push({ collection, id, reason: 'missing' });
          break;
        }
        const draft = structuredClone(source) as JsonObject;
        p.apply(draft);
        if (!isJsonObject(draft)) throw new TypeError('update must leave a JSON object');
        const local = canonicalPayload(collection, assertJsonPayload(draft));
        if (collection !== 'settings' && !fieldEqual('id', local.id, source.id)) {
          throw new TypeError(`An update cannot change ${collection} ${id}'s payload.id.`);
        }
        assertWireValues(collection, local, source);
        if (!entry) {
          const base = canonicalPayload(collection, row!.payload);
          if (deepEqual(local, base)) break; // nothing actually changed
          stage(collection, id, {
            collection,
            id: row!.id,
            kind: 'save',
            basePayload: keepsMergeBase(collection) ? base : null,
            baseVersion: row!.version,
            localPayload: local,
            modifiedAt,
            attempts: 0,
            state: 'pending',
            rev: nextRev(),
          });
        } else {
          if (entry.localPayload && deepEqual(local, entry.localPayload)) break;
          const next: OutboxEntry = { ...entry, localPayload: local, modifiedAt, state: 'pending', rev: nextRev() };
          delete next.error;
          stage(collection, id, next);
        }
        break;
      }
      case 'delete': {
        if (entry?.kind === 'delete') break;
        if (!entry && !row) {
          result.skipped.push({ collection, id, reason: 'missing' });
          break;
        }
        if (!row && entry && entry.baseVersion === null && entry.attempts === 0) {
          // Created here and never sent: nothing exists on the server, just forget it.
          stage(collection, id, null);
          break;
        }
        const next: OutboxEntry = {
          collection,
          id: row?.id ?? entry!.id,
          kind: 'delete',
          basePayload: entry ? entry.basePayload : keepsMergeBase(collection) && row ? canonicalPayload(collection, row.payload) : null,
          baseVersion: entry ? entry.baseVersion : row!.version,
          localPayload: null,
          modifiedAt,
          attempts: entry?.attempts ?? 0,
          state: 'pending',
          rev: nextRev(),
        };
        const filePath = entry?.filePath ?? row?.filePath;
        if (filePath) next.filePath = filePath;
        stage(collection, id, next);
        break;
      }
    }
  }

  if (staged.size > 0) replica.updateMeta({ editSeq: rev });
  for (const { collection, id, entry } of staged.values()) {
    if (entry) replica.putEntry(entry);
    else replica.removeEntry(collection, id);
    result.changed++;
  }
  return result;
}

/** Drops an outbox entry (the "Discard" action for a failed edit, or undoing an unsaved one). */
export function discardEntry(replica: Replica, collection: Collection, id: string): boolean {
  return replica.removeEntry(collection, id);
}

/** Re-queues failed entries (a manual "Try again"). Returns how many. */
export function retryFailed(replica: Replica): number {
  let n = 0;
  for (const entry of replica.entries()) {
    if (entry.state !== 'failed') continue;
    const next: OutboxEntry = { ...entry, state: 'pending' };
    delete next.error;
    replica.putEntry(next);
    n++;
  }
  return n;
}

